import { React, useEffect, useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";

import { useHttpClient } from "../../../../shared/hooks/http-hook";
import { AuthContext } from "../../../../shared/context/auth-context";
import { ClinicaContext } from "../../../../shared/context/clinica-context";
import { localeText } from "../../../../shared/util/datatableTranslations";

import { useLoading } from "../../../../shared/context/LoadingContext";
//Lucid icons
import { Trash2, Edit } from "lucide-react";
import "../../../../shared/css/Datatable.scss";
//Antd
import { Popover, Space, Popconfirm } from "antd";
import toast from "react-hot-toast";

const PAGESIZE = 10;
function Datatable() {
  const { startLoading, stopLoading } = useLoading();

  const [data, setData] = useState();
  const [allData, setAllData] = useState();
  const { sendRequest } = useHttpClient();
  const auth = useContext(AuthContext);
  const clinica = useContext(ClinicaContext);

  const navigate = useNavigate();

  const removeAccents = (str) => {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };

  useEffect(() => {
    const fetchClientes = async () => {
      startLoading();
      try {
        let responseData;
        responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/clientes/datagrid/${clinica.clinica._id}`,
          "GET",
          null,
          {
            Authorization: "Bearer " + auth.token,
          }
        );

        setData(responseData.clientes);
        setAllData(responseData.clientes);
      } catch (err) {
        console.error("err", err);
      } finally {
        stopLoading();
      }
    };
    fetchClientes();
  }, [sendRequest, clinica, auth.token]);

  const desativarCliente = async (id) => {
    startLoading();
    try {
      const formData = new FormData();
      formData.append("userId", auth.userId);
      formData.append("clinicaId", clinica.clinica._id);
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/clientes/desativar/${id}`,
        "PATCH",
        formData,
        {
          Authorization: "Bearer " + auth.token,
        }
      );

      setData((prevClientes) => prevClientes.filter((c) => c._id !== id));
      setAllData((prevClientes) => prevClientes.filter((c) => c._id !== id));
      toast.success("Cliente desativado com sucesso!");
    } catch (err) {
      console.error("err", err);
    } finally {
      stopLoading();
    }
  };

  const clienteColumns = [
    {
      field: "nome",
      headerName: "Nome",
      flex: 7,
      renderCell: (params) => {
        return (
          <span className="nomeDataTable">
            {auth.perm.includes("r-cli") && (
              <Link to={`/clientes/${params.row._id}`}>{params.row.nome}</Link>
            )}
            {!auth.perm.includes("r-cli") && params.row.nome}
          </span>
        );
      },
    },
    {
      field: "contacto",
      headerName: "Contacto",
      flex: 3,
    },
    {
      field: "data_nasc",
      headerName: "Data Nascimento",
      flex: 3,
    },
  ];

  const actionColumn = [
    {
      field: "action",
      headerName: "Ações",
      flex: 1.5,
      renderCell: (params) => {
        const content = (
          <div className="dots__menu__popup">
            {auth.perm.includes("u-cli") && (
              <Link
                to={`/clientes/${params.row._id}/edit`}
                style={{ textDecoration: "none" }}
              >
                <div className="popOverMenu--option">
                  <span>Editar</span>
                  <Edit className="popOverMenu--option__icon" />
                </div>
              </Link>
            )}

            {auth.perm.includes("d-cli") && (
              <Popconfirm
                title="Desativação de cliente!"
                description={`Confirma a desativação do cliente? O processo demora alguns segundos.`}
                icon={<Trash2 style={{ color: "red" }} />}
                okText="Sim"
                cancelText="Não"
                onConfirm={desativarCliente.bind(null, params.row._id)}
              >
                <div className="popOverMenu--option">
                  <span>Desativar</span>
                  <Trash2 className="popOverMenu--option__icon" />
                </div>
              </Popconfirm>
            )}
          </div>
        );

        return (
          <div className="cellAction">
            <Space wrap>
              <Popover content={content} trigger="click" placement="bottom">
                <div className="dots__menu">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </Popover>
            </Space>
          </div>
        );
      },
    },
  ];

  const searchHandler = (e) => {
    if (allData) {
      const searchTerms = removeAccents(e.target.value.toLowerCase()).split(
        /\s+/
      );

      const result = allData.filter((cliente) => {
        // Name search (keeping original logic)
        const normalizedNome = removeAccents(cliente.nome.toLowerCase());
        const nomeWords = normalizedNome.split(/\s+/);
        const matchesName = searchTerms.every((term) =>
          nomeWords.some((word) => word.includes(term))
        );

        // Phone search
        const normalizedContacto = cliente.contacto?.replace(/\s+/g, "");
        const matchesPhone = searchTerms.every((term) =>
          normalizedContacto?.includes(term)
        );

        // Return true if either condition is met
        return matchesName || matchesPhone;
      });

      setData(result);
    }
  };

  return (
    <>
      <div className="datatable caixa">
        <div className="datatable__add-button-div datatable__add-button-div--clientes">
          <div className="search__datatable__container">
            <span>Procurar</span>
            <input
              type="search"
              className="search__datatable__input"
              placeholder="Procurar"
              onChange={searchHandler}
            />
          </div>

          {auth.perm.includes("c-cli") && (
            <span
              className="blue-button"
              onClick={() => navigate("/clientes/new")}
            >
              Adicionar Cliente
            </span>
          )}
        </div>
        {data && (
          <DataGrid
            autoWidth
            className="datagrid"
            rows={data}
            columns={clienteColumns.concat(actionColumn)}
            pageSize={PAGESIZE}
            rowsPerPage={PAGESIZE}
            localeText={localeText}
          />
        )}
      </div>
    </>
  );
}

export default Datatable;
